import {
  Box,
  CloseButton,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { mdiMagnify } from '@mdi/js';
import { useSearchOnList } from 'features/common/hooks/search/useSearchOnList';
import { IMemberRole } from 'features/members/services/identity/membersModel';
import { userAccessLimit } from 'features/members/utils/lib';
import { iconByProduct } from 'features/subscriptions/utils';
import {
  useGetMemberDetailsOrganizationQuery,
  useGetMemberDetailsProductsQuery,
  useGetMemberDetailsUserQuery,
} from 'gql/graphql';
import { flatten } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { UserAppAccessLimitAlert } from '../shared/UserAppAccessLimitAlert';
import { getSitecoreAiDamAccessLabel } from '../utils/getAccessOptions';
import MemberDetailsProductAccessRow from './MemberDetailsProductAccessRow';
import { env } from 'features/common/config/envConfig';
import { useGetAllMarketplaceAppTenants } from 'features/common/hooks/useGetAllMarketplaceAppTenants';
import {
  getPortalProductCode,
  isFgaPrimaryDam,
  isTenantVisibleForScaiDamFlag,
  TenantLabel,
} from 'features/common/utils/tenantLabels';
import { useFeature } from 'lib/featureFlags/features';

interface IMemberDetailsProductAccess {
  roles: IMemberRole[];
  userId: string;
  usersLimit?: number;
}

const MEMBER_DETAILS_PRODUCT_ACCESS_SEARCH_FIELDS: Array<
  'label' | 'productCode' | 'title'
> = ['label', 'productCode', 'title'];

const DAM_PRODUCT_CODES = ['dam', 'contenthubdam'];

const getDamIconUrl = (productCode?: string | null) => {
  const code = productCode?.toLowerCase() ?? '';
  return DAM_PRODUCT_CODES.includes(code)
    ? iconByProduct[code].iconUrl
    : undefined;
};

export const MemberDetailsProductAccess = ({
  roles,
  userId,
  usersLimit = userAccessLimit,
}: IMemberDetailsProductAccess) => {
  const { data: organizationData, loading: isOrganizationLoading } =
    useGetMemberDetailsOrganizationQuery({ variables: { env } });
  const { data: userData, loading: isUserLoading } =
    useGetMemberDetailsUserQuery();
  const { data: productData, loading: isProductLoading } =
    useGetMemberDetailsProductsQuery();
  const { data: marketplaceAppData, loading: isMarketplaceAppLoading } =
    useGetAllMarketplaceAppTenants();

  const isScaiDamEnabled = useFeature('SCAI_DAM');

  const isLoading = isOrganizationLoading || isUserLoading || isProductLoading;

  const hasTenants =
    organizationData?.organization?.applications?.totalCount &&
    organizationData.organization.applications.totalCount > 0;

  const hasMarketplaceApps =
    marketplaceAppData?.user?.tenants?.totalCount &&
    marketplaceAppData.user.tenants.totalCount > 0;

  /**
   * Transforms the roles data to be used in MemberDetailsProductAccessRow
   */
  const getTransformedRoles = () => {
    const allRoles = flatten(
      productData?.products.map((product) => product?.roles?.nodes || []),
    );

    return allRoles.map((role) => ({
      scope: role.productCode,
      name: role.name,
      id: `${role.productCode}-${role.name}`,
      description: '',
    }));
  };

  const getTenantsAccessObject = () => {
    const tenantsList =
      organizationData?.organization?.applications?.nodes && hasTenants
        ? organizationData.organization.applications.nodes.map((node) => ({
            [node.id]: 'no-access',
          }))
        : [];

    const marketplaceTenantsList =
      marketplaceAppData?.user?.tenants?.nodes && hasMarketplaceApps
        ? marketplaceAppData?.user?.tenants?.nodes.map((node) => ({
            [node.id]: 'no-access',
          }))
        : [];

    const tenantsObject = Object.assign(
      {},
      ...tenantsList,
      ...marketplaceTenantsList,
    );

    roles.forEach((role) => {
      if (role.tenantId) {
        tenantsObject[role.tenantId] = role.role;
      }
    });

    return tenantsObject;
  };

  const getTenantIconsObject = () => {
    const icons = {} as any;
    if (userData?.user?.applications?.nodes) {
      userData.user.applications.nodes.forEach((node) => {
        if (node.appearance?.web.actions?.nodes) {
          const { icon, name } = node.appearance.web.actions.nodes[0];
          const portalProductCode = getPortalProductCode(
            node.productCode,
            node.labels,
          );
          if (portalProductCode) {
            icons[portalProductCode] = { ...icon, name };
          }
        }
      });
    }

    return icons;
  };

  const tenantsIcons = getTenantIconsObject();

  /**
   * Returns the tenants view model that is used in MemberDetailsProductAccessRow
   */
  const tenantsViewModel = useMemo(() => {
    const tenantsList = organizationData?.organization?.applications?.nodes
      ? organizationData.organization.applications.nodes
          .filter((node) =>
            isTenantVisibleForScaiDamFlag(
              node.productCode,
              node.labels,
              isScaiDamEnabled,
            ),
          )
          .map((node) => {
            const portalProductCode =
              getPortalProductCode(node.productCode, node.labels) ?? '';
            const appearanceIcon = tenantsIcons[portalProductCode];

            return {
              id: node.id,
              label: node.displayName,
              productCode: node.productCode,
              systemId: node.systemId,
              title:
                appearanceIcon?.name ||
                tenantsIcons[node.productCode]?.name ||
                '',
              iconUrl:
                appearanceIcon?.src ||
                getDamIconUrl(portalProductCode) ||
                getDamIconUrl(node.productCode) ||
                '',
              labels: node.labels,
            };
          })
      : [];

    const marketplaceTenantsList = marketplaceAppData?.user?.tenants?.nodes
      ? marketplaceAppData.user.tenants.nodes.map((node) => {
          return {
            id: node.id,
            label: node.displayName,
            productCode: node.productCode,
            systemId: node.systemId,
            title: 'Marketplace App',
            labels: undefined,
            iconUrl:
              getDamIconUrl(node.productCode) ||
              node?.annotations?.find(
                (annotation: any) => annotation.key === 'IconUrl',
              )?.value ||
              '',
          };
        })
      : [];

    return [...tenantsList, ...marketplaceTenantsList];
  }, [tenantsIcons, organizationData, marketplaceAppData, isScaiDamEnabled]);

  const [searchValue, setSearchValue] = useState<string>('');

  const tenantsAccessObject = getTenantsAccessObject();

  const filteredTenantList = useSearchOnList(
    tenantsViewModel,
    MEMBER_DETAILS_PRODUCT_ACCESS_SEARCH_FIELDS,
    searchValue,
  );

  const hasNoApps = filteredTenantList.length === 0;
  const isSearching = Boolean(searchValue);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchInputValue = event.target.value;
    setSearchValue(searchInputValue);
  };

  const onClearHandler = () => {
    setSearchValue('');
  };

  const debouncedChangeHandler = useMemo(
    () => debounce(onChangeHandler, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description returns selected tenant option, if nothing is selected return no-access
   * @param selected
   */
  const getSelectedTenantOption = (
    selected: string,
    productCode?: string,
    labels?: TenantLabel[] | null,
  ) => {
    if (selected === 'no-access') {
      return { value: 'no-access', label: 'No access' };
    }
    return {
      value: selected,
      label: isFgaPrimaryDam(productCode, labels)
        ? getSitecoreAiDamAccessLabel(selected) ?? selected
        : selected,
    };
  };

  const getTenantData = (id: string) => {
    const tenantAccessValue = tenantsAccessObject[id];
    const tenant = tenantsViewModel.find((tenant) => tenant.id == id);
    const selectedValue = getSelectedTenantOption(
      tenantAccessValue,
      tenant?.productCode,
      tenant?.labels,
    );

    return { tenant, selectedValue };
  };

  const tenantsWithAccess = useMemo(
    () =>
      tenantsViewModel.filter(
        ({ id }) => tenantsAccessObject[id] !== 'no-access',
      ),
    [tenantsViewModel, tenantsAccessObject],
  );

  const accessRowRoles = getTransformedRoles();
  const userHasReachedTheLimit = tenantsWithAccess.length >= usersLimit;

  return isLoading ? (
    <Stack gap={5}>
      <Skeleton maxWidth='sm' height='10' />
      <Skeleton maxWidth='lg' height='10' />
      <Skeleton maxWidth='lg' height='10' />
      <Skeleton maxWidth='lg' height='10' />
    </Stack>
  ) : (
    <Box>
      {hasTenants || hasMarketplaceApps ? (
        <>
          <InputGroup mb='4' maxWidth='sm'>
            <InputLeftElement
              pointerEvents='none'
              // TODO change this after we open Input, InputElement props
              children={
                <Icon>
                  <path d={mdiMagnify} />
                </Icon>
              }
            />
            <Input
              data-testid='tenant-search-input'
              value={searchValue}
              onChange={onChangeHandler}
              placeholder='Search apps'
            />
            {searchValue.length > 0 && (
              <InputRightElement>
                <Tooltip label='Clear search'>
                  <CloseButton
                    onClick={onClearHandler}
                    data-testid='tenant-search-clear-button'
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>
          {userHasReachedTheLimit ? <UserAppAccessLimitAlert /> : null}
          {filteredTenantList.map(({ id }) => {
            const { tenant, selectedValue } = getTenantData(id);

            if (!tenant || !accessRowRoles) return null;

            const shouldDisableAccessRow =
              userHasReachedTheLimit && selectedValue.value === 'no-access';
            return (
              <MemberDetailsProductAccessRow
                tenant={tenant}
                selectedValue={selectedValue}
                userId={userId}
                isDisabled={shouldDisableAccessRow}
                key={id}
                roles={accessRowRoles}
              />
            );
          })}
          {hasNoApps && isSearching && (
            <Text variant='subtle' data-testid='no-apps-found'>
              No apps found matching your search.
            </Text>
          )}
          {isMarketplaceAppLoading && (
            <Stack gap={5}>
              <Skeleton maxWidth='lg' height='10' />
              <Skeleton maxWidth='lg' height='10' />
              <Skeleton maxWidth='lg' height='10' />
            </Stack>
          )}
        </>
      ) : (
        <Text data-testid='no-app-access'>
          There are no apps set up yet. You will need to update the product
          access after provisioning.
        </Text>
      )}
    </Box>
  );
};
